import type { ReviewItem } from '~~/domains/graphql';
import { useHotelReviewsQuery } from '~~/domains/graphql';

type HotelReviews = {
  allReviewsLoad: Readonly<Ref<boolean>>;
  loadMore: () => void;
  reviews: ComputedRef<ReviewItem[]>;
  size: Readonly<Ref<number>>;
  isLoading: Readonly<Ref<boolean>>;
};

export function useHotelReviews(hotelId: string): HotelReviews {
  const offset = ref(0);
  const allReviewsLoad = ref(false);
  const size = ref(3);

  const { fetchMore, result, loading } = useHotelReviewsQuery({
    hotelId,
    offset: offset.value,
    size: size.value,
  });

  const reviews = computed(
    () =>
      (result.value?.hotel?.reviewsList ?? []).filter(Boolean) as ReviewItem[],
  );

  const loadMore = () => {
    offset.value += size.value;
    fetchMore({
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.hotel?.reviewsList) {
          allReviewsLoad.value = true;
          return {
            ...previousResult,
          };
        }

        if (fetchMoreResult.hotel?.reviewsList?.length < 3) {
          allReviewsLoad.value = true;
        }

        return {
          ...previousResult,
          hotel: {
            ...previousResult.hotel,
            id: previousResult.hotel?.id as string,
            reviewsList: reviews.value.concat(
              (fetchMoreResult.hotel?.reviewsList ?? []) as ReviewItem[],
            ),
          },
        };
      },
      variables: { hotelId, offset: offset.value, size: size.value },
    });
  };

  return {
    allReviewsLoad: readonly(allReviewsLoad),
    loadMore,
    reviews,
    size: readonly(size),
    isLoading: loading,
  };
}
